import { useState } from 'react';
import { Info, X } from 'lucide-react';
import { Sheet, SheetContent } from './ui/sheet';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Tiny info icon that opens a bottom sheet explaining TDEE for users
 * who want to understand the science behind the "Sport bonus" in
 * the calorie balance.
 */
export function TDEEInfoButton({ className = '' }) {
    const { language } = useLanguage();
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={language === 'de' ? 'Was ist TDEE?' : 'What is TDEE?'}
                data-testid="tdee-info-btn"
                className={`text-muted-foreground/70 hover:text-primary transition-colors ${className}`}
            >
                <Info className="w-3 h-3" />
            </button>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="bottom" className="h-[85vh] flex flex-col p-0">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-background z-10">
                        <h2 className="font-display text-base font-bold">
                            {language === 'de' ? 'Wie zählt Sport in der Kalorien-Bilanz?' : 'How are sport calories counted?'}
                        </h2>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            data-testid="tdee-info-close"
                            className="p-1 rounded-full hover:bg-muted"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5 text-sm leading-relaxed">
                        {/* Intro */}
                        <p className="text-muted-foreground">
                            {language === 'de'
                                ? 'Damit du verstehst, was hinter den Zahlen steckt — eine kurze Einführung in die Energiebilanz deines Körpers.'
                                : 'A quick primer on how your body actually spends energy — so the numbers make sense.'}
                        </p>

                        {/* TDEE Definition */}
                        <section className="space-y-2">
                            <h3 className="font-semibold text-base">
                                TDEE — Total Daily Energy Expenditure
                            </h3>
                            <p>
                                {language === 'de'
                                    ? 'Dein gesamter Tagesenergieverbrauch in Kalorien. Setzt sich aus 4 Komponenten zusammen:'
                                    : 'Your total daily energy spend in calories. Made up of 4 components:'}
                            </p>
                            <div className="space-y-2 mt-2">
                                <div className="rounded-xl bg-muted/40 p-3">
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-semibold">BMR</span>
                                        <span className="text-xs text-muted-foreground">~60–70%</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {language === 'de'
                                            ? 'Grundumsatz — was dein Körper im Liegen verbrennt (Herz, Atmung, Zellerneuerung).'
                                            : 'Basal metabolic rate — what your body burns at rest (heart, breathing, cell turnover).'}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-muted/40 p-3">
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-semibold">TEF</span>
                                        <span className="text-xs text-muted-foreground">~10%</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {language === 'de'
                                            ? 'Thermic Effect of Food — Energie zum Verdauen der Nahrung.'
                                            : 'Thermic effect of food — energy needed to digest meals.'}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-muted/40 p-3">
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-semibold">NEAT</span>
                                        <span className="text-xs text-muted-foreground">~15–30%</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {language === 'de'
                                            ? 'Non-Exercise Activity — Alltagsbewegung: Stehen, Gehen, Putzen, Tippen, Zappeln. Hier fließen Schritte rein.'
                                            : 'Non-exercise activity — daily movement: standing, walking, chores, typing, fidgeting. Steps live here.'}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-muted/40 p-3">
                                    <div className="flex items-baseline justify-between">
                                        <span className="font-semibold">EAT</span>
                                        <span className="text-xs text-muted-foreground">~5%</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {language === 'de'
                                            ? 'Exercise Activity — gezielte Workouts.'
                                            : 'Exercise activity — deliberate workouts.'}
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground italic pt-1">
                                {language === 'de'
                                    ? 'Isst du genau dein TDEE → Gewicht bleibt stabil. Weniger → Abnehmen. Mehr → Aufbauen.'
                                    : 'Eat exactly your TDEE → weight stays stable. Less → lose. More → gain.'}
                            </p>
                        </section>

                        {/* Why 1:1 is liberal */}
                        <section className="space-y-2">
                            <h3 className="font-semibold text-base">
                                {language === 'de' ? 'Warum die Sport-Bonus-Rechnung 1:1 grosszügig ist' : 'Why the 1:1 sport bonus is generous'}
                            </h3>
                            <ul className="space-y-2 pl-1">
                                <li className="flex gap-2">
                                    <span className="text-primary font-bold">·</span>
                                    <span>
                                        {language === 'de'
                                            ? <><strong>Wearables überschätzen</strong> Aktivitätskalorien um 20–50 % (Stanford-Studie 2017: Apple Watch ⌀ +27 %, Fitbit ⌀ +30 %).</>
                                            : <><strong>Wearables overestimate</strong> activity calories by 20–50 % (Stanford 2017: Apple Watch avg +27 %, Fitbit avg +30 %).</>}
                                    </span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-primary font-bold">·</span>
                                    <span>
                                        {language === 'de'
                                            ? <><strong>NEAT ist im Tagesziel oft schon drin</strong> — wenn dein Kalorienziel auf "moderat aktiv" oder höher kalibriert ist, sind ~250–500 kcal Alltagsbewegung bereits eingerechnet.</>
                                            : <><strong>NEAT is usually already in your goal</strong> — if your goal is set to "moderately active" or higher, ~250–500 kcal of daily movement are already baked in.</>}
                                    </span>
                                </li>
                                <li className="flex gap-2">
                                    <span className="text-primary font-bold">·</span>
                                    <span>
                                        {language === 'de'
                                            ? <><strong>Doppelzählung</strong>: NEAT + Schritte-Bonus von BESTBUDDY zusammen können dazu führen, dass du täglich 300–700 kcal mehr „verdienst", als du tatsächlich extra verbrannt hast.</>
                                            : <><strong>Double-counting risk</strong>: NEAT + BESTBUDDY's step bonus combined can credit you 300–700 kcal/day more than you actually burned beyond baseline.</>}
                                    </span>
                                </li>
                            </ul>
                        </section>

                        {/* App benchmark */}
                        <section className="space-y-2">
                            <h3 className="font-semibold text-base">
                                {language === 'de' ? 'Wie machen es andere Apps?' : 'How other apps handle this'}
                            </h3>
                            <div className="rounded-xl border border-border overflow-hidden text-xs">
                                <div className="grid grid-cols-2 bg-muted/60 px-3 py-2 font-semibold">
                                    <span>App</span>
                                    <span>{language === 'de' ? 'Sport-Anrechnung' : 'Sport credit'}</span>
                                </div>
                                <div className="grid grid-cols-2 px-3 py-2 border-t border-border">
                                    <span>MyFitnessPal</span>
                                    <span>100 % (1:1)</span>
                                </div>
                                <div className="grid grid-cols-2 px-3 py-2 border-t border-border bg-muted/20">
                                    <span>Lose It!</span>
                                    <span>100 % (1:1)</span>
                                </div>
                                <div className="grid grid-cols-2 px-3 py-2 border-t border-border">
                                    <span>Cronometer</span>
                                    <span>{language === 'de' ? '0% / 50% / 100% wählbar' : '0% / 50% / 100% selectable'}</span>
                                </div>
                                <div className="grid grid-cols-2 px-3 py-2 border-t border-border bg-muted/20">
                                    <span>Garmin Connect</span>
                                    <span>{language === 'de' ? 'TDEE-Ansatz (kein Bonus)' : 'TDEE-based (no bonus)'}</span>
                                </div>
                                <div className="grid grid-cols-2 px-3 py-2 border-t border-border">
                                    <span><strong>BESTBUDDY</strong></span>
                                    <span><strong>100 % (1:1)</strong></span>
                                </div>
                            </div>
                        </section>

                        {/* Recommendation */}
                        <section className="rounded-xl bg-primary/10 border border-primary/30 p-3 text-xs leading-relaxed">
                            <p className="font-semibold mb-1">
                                {language === 'de' ? '🐺 Buddy-Tipp' : '🐺 Buddy tip'}
                            </p>
                            <p>
                                {language === 'de'
                                    ? 'Wenn du Fett abbaust oder dein Gewicht hältst: betrachte den Sport-Bonus eher als „nice to have" und iss nur ~50 % davon zusätzlich auf. Wenn du Muskeln aufbaust (Bulking), kannst du 100 % nutzen — du brauchst den Kalorienüberschuss ohnehin.'
                                    : 'If you\'re cutting fat or maintaining weight: treat the sport bonus as "nice to have" and only eat back ~50 % of it. If you\'re bulking, you can take 100 % — you need the surplus anyway.'}
                            </p>
                        </section>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
